import { useMemo, useState } from "react";
import {
    digitsOnly,
    REQUISITE_FIELD_RULES,
    validateEmploymentRequisites,
} from "../../utils/employmentRequisites";
import { useAlertContext } from "../../context/AlertContext";

const GROUPS = [
    {
        title: "Личные данные",
        fields: [
            ["birth_date", "Дата рождения", "date"],
            ["snils_number", "СНИЛС"],
            ["inn_number", "ИНН"],
        ],
    },
    {
        title: "Паспорт и адреса",
        fields: [
            ["passport_series", "Серия паспорта"],
            ["passport_number", "Номер паспорта"],
            ["passport_issue_date", "Дата выдачи", "date"],
            ["passport_department_code", "Код подразделения"],
            ["passport_issued_by", "Кем выдан", "textarea"],
            ["registration_address", "Адрес регистрации", "textarea"],
            ["actual_residence_address", "Фактический адрес проживания", "textarea"],
        ],
    },
    {
        title: "Банковские реквизиты",
        fields: [
            ["bank_account", "Расчётный счёт"],
            ["bank_name", "Наименование банка"],
            ["bank_bik", "БИК"],
            ["correspondent_account", "Корреспондентский счёт"],
        ],
    },
];

const FIELD_NAMES = GROUPS.flatMap((group) => group.fields.map(([name]) => name));

function editableValue(name, value) {
    const rule = REQUISITE_FIELD_RULES[name] || {};
    if (rule.digits) return digitsOnly(value);
    return value == null ? "" : String(value);
}

function createForm(details) {
    return Object.fromEntries(
        FIELD_NAMES.map((name) => [name, editableValue(name, details?.[name])]),
    );
}

function EditorField({ name, label, type = "text", value, error, onChange }) {
    const rule = REQUISITE_FIELD_RULES[name] || {};
    const count = rule.digits ? digitsOnly(value).length : String(value || "").length;
    const limit = rule.digits || rule.maxLength;
    const id = `employee-document-${name}`;
    const common = {
        id,
        "data-testid": id,
        value,
        onChange: (event) => {
            const next = rule.digits
                ? digitsOnly(event.target.value).slice(0, rule.digits)
                : event.target.value;
            onChange(name, next);
        },
        ...(rule.digits ? { inputMode: "numeric", maxLength: rule.digits } : {}),
        ...(rule.maxLength ? { maxLength: rule.maxLength } : {}),
        ...(type === "date" ? { min: "1900-01-01", max: new Date().toISOString().slice(0, 10) } : {}),
        ...(error ? { "aria-invalid": true, "aria-describedby": `${id}-error` } : {}),
        className: `mt-2 w-full rounded-xl border bg-white px-4 py-3 text-base text-slate-900 shadow-sm outline-none transition focus:ring-2 ${error ? "border-red-500 focus:ring-red-100" : "border-slate-300 focus:border-slate-700 focus:ring-slate-200"}`,
    };
    return (
        <label htmlFor={id} className={type === "textarea" ? "block md:col-span-2" : "block"}>
            <span className="flex items-center justify-between gap-3 text-sm font-semibold text-slate-700">
                <span>{label}</span>
                {limit ? <span className={error ? "text-red-600" : "font-normal text-slate-500"}>{count}/{limit} {rule.digits ? "цифр" : "символов"}</span> : null}
            </span>
            {type === "textarea" ? <textarea {...common} rows={3} /> : <input {...common} type={type} />}
            {error ? <span id={`${id}-error`} className="mt-1 block text-sm font-medium text-red-600">{error}</span> : null}
        </label>
    );
}

export default function EmployeeDocumentDetailsEditor({ details, onSave, onCancel }) {
    const initial = useMemo(() => createForm(details), [details]);
    const [form, setForm] = useState(initial);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const { showAlert } = useAlertContext();

    const change = (name, value) => {
        setForm((current) => ({ ...current, [name]: value }));
        setErrors((current) => {
            if (!current[name]) return current;
            const next = { ...current };
            delete next[name];
            return next;
        });
    };

    const submit = async (event) => {
        event.preventDefault();
        const payload = {};
        FIELD_NAMES.forEach((name) => {
            if (form[name] !== initial[name]) payload[name] = form[name] || null;
        });
        if (!Object.keys(payload).length) {
            showAlert("В данных из документов нет изменений", "info");
            return;
        }

        const validationInput = { ...payload };
        if ("birth_date" in payload || "passport_issue_date" in payload) {
            validationInput.birth_date = form.birth_date;
            validationInput.passport_issue_date = form.passport_issue_date;
        }
        const validationErrors = validateEmploymentRequisites(validationInput);
        if (Object.keys(validationErrors).length) {
            setErrors(validationErrors);
            showAlert(Object.values(validationErrors)[0], "error");
            return;
        }

        setSaving(true);
        try {
            await onSave(payload);
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={submit} className="mt-5 space-y-4" data-testid="employee-document-details-editor">
            <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm leading-6 text-blue-900">
                Исправления сохраняются как проверенные HR и используются при следующем формировании договоров. Поля без изменений не перезаписываются.
            </div>
            {GROUPS.map((group) => (
                <fieldset key={group.title} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 md:p-5">
                    <legend className="px-2 text-base font-bold text-slate-900">{group.title}</legend>
                    <div className="mt-2 grid grid-cols-1 gap-4 md:grid-cols-2">
                        {group.fields.map(([name, label, type]) => (
                            <EditorField key={name} name={name} label={label} type={type} value={form[name]} error={errors[name]} onChange={change} />
                        ))}
                    </div>
                </fieldset>
            ))}
            <div className="flex flex-wrap justify-end gap-3">
                <button type="button" onClick={onCancel} disabled={saving} className="rounded-xl border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">Отмена</button>
                <button type="submit" disabled={saving} className="rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50" data-testid="save-employee-document-details">
                    {saving ? "Сохранение…" : "Сохранить исправления"}
                </button>
            </div>
        </form>
    );
}
